// Notification utilities for chat messages and inquiries

class NotificationManager {
  constructor() {
    this.permission = 'default';
    this.isSupported = 'Notification' in window;
    this.init();
  }

  async init() {
    if (!this.isSupported) {
      return;
    }

    // Check current permission
    this.permission = Notification.permission;
    
    // If permission is default, we'll request it when needed
    if (this.permission === 'default') {
    }
  }

  async requestPermission() {
    if (!this.isSupported) {
      return false;
    }

    if (this.permission === 'granted') {
      return true;
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      
      if (permission === 'granted') {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  }

  async showNotification(title, options = {}) {
    if (!this.isSupported || this.permission !== 'granted') {
      return null;
    }

    try {
      const defaultOptions = {
        icon: '/images/VESTRIOZ.png',
        badge: '/images/VESTRIOZ.png',
        tag: 'vestrioz-chat',
        requireInteraction: false,
        silent: false,
        ...options
      };

      const notification = new Notification(title, defaultOptions);
      
      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      return notification;
    } catch (error) {
      return null;
    }
  }

  async showChatNotification(senderName, message, chatId, listingName) {
    const title = `💬 New message from ${senderName}`;
    const body = message.length > 50 ? `${message.substring(0, 50)}...` : message;
    
    const options = {
      body: `${body}\n📦 About: ${listingName}`,
      tag: `chat-${chatId}`,
      data: {
        type: 'chat',
        chatId: chatId,
        senderName: senderName,
        listingName: listingName
      },
      actions: [
        {
          action: 'reply',
          title: 'Reply',
          icon: '/images/reply-icon.png'
        },
        {
          action: 'view',
          title: 'View Chat',
          icon: '/images/view-icon.png'
        }
      ]
    };

    const notification = await this.showNotification(title, options);
    
    if (notification) {
      notification.onclick = () => {
        window.focus();
        // Navigate to chat page
        window.location.href = '/chat';
        notification.close();
      };
    }

    return notification;
  }

  async showImageNotification(senderName, caption, chatId, listingName) {
    const title = `📷 ${senderName} sent an image`;
    const body = caption ? 
      `${caption.length > 50 ? caption.substring(0, 50) + '...' : caption}\n📦 About: ${listingName}` :
      `📦 About: ${listingName}`;
    
    const options = {
      body: body,
      tag: `chat-image-${chatId}`,
      data: {
        type: 'chat-image',
        chatId: chatId,
        senderName: senderName,
        listingName: listingName
      }
    };

    const notification = await this.showNotification(title, options);
    
    if (notification) {
      notification.onclick = () => {
        window.focus();
        window.location.href = '/chat';
        notification.close();
      };
    }

    return notification;
  }

  async showInquiryNotification(buyerName, listingName, listingId) {
    const title = `🔔 New inquiry from ${buyerName}`;
    const body = `Someone is interested in your listing: ${listingName}`;
    
    const options = {
      body: body,
      tag: `inquiry-${listingId}`,
      data: {
        type: 'inquiry',
        listingId: listingId,
        buyerName: buyerName,
        listingName: listingName
      }
    };

    const notification = await this.showNotification(title, options);
    
    if (notification) {
      notification.onclick = () => {
        window.focus();
        window.location.href = '/chat';
        notification.close();
      };
    }

    return notification;
  }

  // Check if notifications are enabled
  isEnabled() {
    return this.isSupported && this.permission === 'granted';
  }

  // Get permission status
  getPermissionStatus() {
    return this.permission;
  }
}

// Create singleton instance
const notificationManager = new NotificationManager();

export default notificationManager;

// Helper functions for easy use
export const requestNotificationPermission = () => {
  return notificationManager.requestPermission();
};

export const showChatNotification = (senderName, message, chatId, listingName) => {
  return notificationManager.showChatNotification(senderName, message, chatId, listingName);
};

export const showImageNotification = (senderName, caption, chatId, listingName) => {
  return notificationManager.showImageNotification(senderName, caption, chatId, listingName);
};

export const showInquiryNotification = (buyerName, listingName, listingId) => {
  return notificationManager.showInquiryNotification(buyerName, listingName, listingId);
};

export const isNotificationEnabled = () => {
  return notificationManager.isEnabled();
};

export const getNotificationPermission = () => {
  return notificationManager.getPermissionStatus();
};